import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import './AdminLedger.css';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function AdminLedger() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchingLedger, setFetchingLedger] = useState(false);
  
  // Data States
  const [filteredLedger, setFilteredLedger] = useState([]);
  const [defaultersList, setDefaultersList] = useState([]);
  const [matrixData, setMatrixData] = useState(null);

  // Filters
  const [selectedFY, setSelectedFY] = useState("");
  const [dateFilterType, setDateFilterType] = useState('MONTH');
  const [searchMonth, setSearchMonth] = useState(""); 
  const [searchDate, setSearchDate] = useState("");
  const [searchPropType, setSearchPropType] = useState("ALL");
  const [searchHouse, setSearchHouse] = useState("");
  const [defaulterPropType, setDefaulterPropType] = useState("ALL"); 

  // Matrix Filters
  const [matrixView, setMatrixView] = useState("MONTHLY"); 
  const [gridMonth, setGridMonth] = useState("");
  const [gridPropType, setGridPropType] = useState("ALL");

  // UI States
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedPlotDetails, setSelectedPlotDetails] = useState(null);

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  const formatProp = (pt) => pt === 'c_buildings' ? 'Comm. Bldg' : pt === 'n_home' ? 'Nursing Home' : pt;

  // 1. Initial Load: Set defaults & Fetch the Master Defaulters Scoreboard ONLY
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const today = new Date();
        const cm = today.getMonth() + 1;
        const cy = today.getFullYear();
        const defaultStartYear = cm >= 4 ? cy : cy - 1;
        const currentFY = `${defaultStartYear}-${defaultStartYear + 1}`;
        
        setSelectedFY(currentFY);
        setGridMonth(`${cy}-${String(cm).padStart(2, '0')}`);

        // Fetch exactly 1 document for defaulters
        const defDoc = await getDoc(doc(db, 'system_metadata', 'master_defaulters'));
        if (defDoc.exists()) {
            const defData = defDoc.data();
            // Convert dictionary to array and sort by highest debt
            const dList = Object.values(defData).sort((a,b) => b.due - a.due);
            setDefaultersList(dList);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
      setLoading(false);
    };
    fetchInitialData();
  }, []);

  // 2. Fetch Ledger On Demand (Server-Side Filtering)
  const handleFetchLedger = async () => {
    setFetchingLedger(true);
    try {
        let q = collection(db, 'ledger_entries');
        const constraints = [where("type", "==", "PAYMENT")];

        if (searchPropType !== "ALL") constraints.push(where("propertyType", "==", searchPropType));
        if (searchHouse.trim() !== "") constraints.push(where("houseNo", "==", searchHouse.trim()));

        // Server-Side Date Filtering
        if (dateFilterType === "DATE" && searchDate) {
            constraints.push(where("dateOnly", "==", searchDate));
        } else if (dateFilterType === "MONTH" && searchMonth) {
            constraints.push(where("dateOnly", ">=", `${searchMonth}-01`));
            constraints.push(where("dateOnly", "<=", `${searchMonth}-31`));
        } else if (dateFilterType === "FY" && selectedFY) {
            const startYear = selectedFY.substring(0, 4);
            const endYear = selectedFY.substring(5, 9);
            constraints.push(where("dateOnly", ">=", `${startYear}-04-01`));
            constraints.push(where("dateOnly", "<=", `${endYear}-03-31`));
        }

        const finalQuery = query(q, ...constraints);
        const snap = await getDocs(finalQuery);
        let fetchedLedger = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Strict Sorting: Date > PropertyType > PropertyNumber
        fetchedLedger = fetchedLedger.filter(e => (e.amountSigned || 0) > 0).sort((a, b) => {
            const dateA = new Date(a.dateOnly || 0).getTime();
            const dateB = new Date(b.dateOnly || 0).getTime();
            if (dateB !== dateA) return dateB - dateA;

            const propA = a.propertyType || "";
            const propB = b.propertyType || "";
            if (propA !== propB) return propA.localeCompare(propB);

            const houseA = String(a.houseNo || "");
            const houseB = String(b.houseNo || "");
            return houseA.localeCompare(houseB, undefined, { numeric: true, sensitivity: 'base' });
        });

        setFilteredLedger(fetchedLedger);
    } catch (error) {
        alert("Failed to fetch ledger data: " + error.message);
    }
    setFetchingLedger(false);
  };

  // 3. Fetch Matrix Grid On Demand (Triggered by Search Button)
  const handleFetchMatrix = async () => {
      let docId = "";
      if (matrixView === 'MONTHLY' && gridMonth) {
          docId = `matrix_monthly_${gridMonth}`;
      } else if (matrixView === 'YEARLY' && selectedFY) {
          const fyStartYear = parseInt(selectedFY.substring(0, 4));
          docId = `matrix_yearly_${fyStartYear - 1}`;
      }

      if (docId) {
          try {
              const mDoc = await getDoc(doc(db, 'matrix_metadata', docId));
              if (mDoc.exists()) setMatrixData(mDoc.data());
              else setMatrixData(null); 
          } catch(e) { console.error("Matrix fetch error:", e); }
      }
  };

  // Generate Dropdown Lists Dynamically
  const currentYear = new Date().getFullYear();
  const fyList = [
    `${currentYear+1}-${currentYear+2}`, 
    `${currentYear}-${currentYear+1}`, 
    `${currentYear-1}-${currentYear}`,
    `${currentYear-2}-${currentYear-1}` // <-- Adds the 2024-2025 Financial Year
  ];
  
  const generateMonthsForFY = (fyStr) => {
      if(!fyStr) return [];
      const startY = parseInt(fyStr.substring(0, 4));
      const endY = parseInt(fyStr.substring(5, 9));
      const m = [];
      for(let i=4; i<=12; i++) m.push(`${startY}-${String(i).padStart(2,'0')}`);
      for(let i=1; i<=3; i++) m.push(`${endY}-${String(i).padStart(2,'0')}`);
      return m.reverse();
  };
  const monthList = generateMonthsForFY(selectedFY);

  const filteredDefaulters = defaultersList.filter(d => defaulterPropType === "ALL" || d.propertyType === defaulterPropType);

  if (loading) return <div style={{padding:'50px', textAlign:'center'}}>Loading Master Ledger...</div>;


  const exportToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape for wide tables
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

    // --- 1. PROFESSIONAL HEADER ---
    doc.setFontSize(16); // Increased to 16pt
    doc.setTextColor(44, 62, 80);
    doc.setFont("helvetica", "bold"); 
    doc.text("THE BATHINDA HOUSING COMPLEX COOPERATIVE HOUSE BUILDING SOCIETY LIMITED", pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(12); // Increased to 12pt
    doc.setFont("helvetica", "normal");
    doc.text("(PAN: AAFAT6965P) Ledger Report", pageWidth / 2, 22, { align: 'center' });

    // --- 2. DYNAMIC REPORT HIGHLIGHTS ---
    let dateStr = "All Time";
    if (dateFilterType === "FY") dateStr = `FY ${selectedFY || 'All'}`;
    else if (dateFilterType === "MONTH") dateStr = `Month: ${searchMonth || 'All'}`;
    else if (dateFilterType === "DATE") dateStr = `Date: ${searchDate || 'All'}`;

    let propStr = searchPropType === "ALL" ? "All Properties" : searchPropType;
    let houseStr = searchHouse ? `Plot: ${searchHouse}` : "All Plots";
    
    doc.setFontSize(10); // Increased to 10pt
    doc.setTextColor(80, 80, 80);
    doc.text(`Report Highlights: Period: ${dateStr}  |  Property: ${propStr}  |  ${houseStr}`, 14, 32);
    doc.text(`Generated on: ${today}`, pageWidth - 14, 32, { align: 'right' });

    // --- NEW: PUNJABI LEGEND VIA NATIVE CANVAS (Bypasses jsPDF encoding issues) ---
    const canvas = document.createElement('canvas');
    canvas.width = 2200; // High resolution for crisp printing
    canvas.height = 80;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#646464'; // Matches your gray text
    ctx.font = '34px sans-serif'; // The browser natively shapes the Gurmukhi script
    ctx.fillText("Legend:  Arr: ਬਕਾਇਆ  |  Adv: ਐਡਵਾਂਸ  |  Manual: ਆਫਿਸ ਦੁਆਰਾ  |  Online: ਔਨਲਾਇਨ  |  Pen:ਪੈਨਲਟੀ   |  W/S: ਪਾਣੀ-ਸੀਵਰੇਜ  |  Srv: ਸਰਵਿਸ", 0, 50);
    
    const legendImgData = canvas.toDataURL('image/png');
    // Add the perfectly rendered text as an image (X:14, Y:35, W:165mm, H:6mm)
    doc.addImage(legendImgData, 'PNG', 14, 35, 165, 6);

    // --- 3. CALCULATE MATH & BUILD ROWS ---
    let totalGross = 0;
    let totalFees = 0;
    let totalNet = 0;

    const tableRows = filteredLedger.map(entry => {
      const gross = entry.amountSigned / 100;
      const fee = (entry.metrics?.platformFee || 0) / 100;
      const net = gross - fee; 

      totalGross += gross;
      totalFees += fee;
      totalNet += net;

      const m = entry.metrics || {};

      // ---> NEW: Build Multi-line Description with Exact Months <---
      let finalDesc = entry.description || "Online Payment";
      const monthDetails = [];
      let baseStr = "";

      const isLegacyData = m.swArrears === undefined; 
      
      if (m.arrearsMonthsCleared && m.arrearsMonthsCleared.length > 0) {
          monthDetails.push(`Arr: ${m.arrearsMonthsCleared.join(', ')}`);
          if (!isLegacyData && (m.swArrears > 0 || m.serviceArrears > 0)) {
              const moCount = m.arrearsMonthsCleared.length;
              baseStr = `[Base/mo] W/S Rs.${(m.swArrears/100/moCount).toFixed(0)} | Srv Rs.${(m.serviceArrears/100/moCount).toFixed(0)}`;
          }
      } 
      if (m.advanceMonthsGenerated && m.advanceMonthsGenerated.length > 0) {
          monthDetails.push(`Adv: ${m.advanceMonthsGenerated.join(', ')}`);
          if (!isLegacyData && !baseStr && (m.swAdvance > 0 || m.serviceAdvance > 0)) {
              const moCount = m.advanceMonthsGenerated.length;
              baseStr = `[Base/mo] W/S Rs.${(m.swAdvance/100/moCount).toFixed(0)} | Srv Rs.${(m.serviceAdvance/100/moCount).toFixed(0)}`;
          }
      }
      if (m.advanceYearsGenerated && m.advanceYearsGenerated.length > 0) {
          monthDetails.push(`Adv Yr: ${m.advanceYearsGenerated.join(', ')}`);
          if (!isLegacyData && !baseStr && m.maintAdvance > 0) {
              const yrCount = m.advanceYearsGenerated.length;
              baseStr = `[Base/yr] Maint Rs.${(m.maintAdvance/100/yrCount).toFixed(0)}`;
          }
      }
      
      if (monthDetails.length > 0) {
          finalDesc += `\n[${monthDetails.join(' | ')}]`;
      }
      if (baseStr) {
          finalDesc += `\n${baseStr}`;
      }

      // Format the Math Breakdown String
      const mathParts = [];
      
      if (isLegacyData) {
          // --- LEGACY DATA FALLBACK (For payments made before this update) ---
          if (m.baseArrearsCollected) mathParts.push(`Arr: Rs.${(m.baseArrearsCollected/100).toFixed(0)}`);
          if (m.penaltyCollected) mathParts.push(`Pen: Rs.${(m.penaltyCollected/100).toFixed(0)}`);
          if (m.advanceCollected) mathParts.push(`Adv: Rs.${((m.advanceCollected + (m.advanceDiscount || 0))/100).toFixed(0)}`);
          if (m.advanceDiscount) mathParts.push(`Disc: -Rs.${(m.advanceDiscount/100).toFixed(0)}`);
      } else {
          // --- NEW DYNAMIC SPLIT SYSTEM (EMOJI-FREE FOR PDF) ---
          // 1. ARREARS BREAKDOWN
          if (m.arrearsMonthsCleared && m.arrearsMonthsCleared.length > 0) {
             mathParts.push(`[ARREARS: ${m.arrearsMonthsCleared.length} Mo]`);
             
             let totalBaseArrears = (m.swArrears || 0) + (m.serviceArrears || 0);
             let swPen = 0, srvPen = 0;
             if (totalBaseArrears > 0 && m.penaltyCollected > 0) {
                 swPen = m.penaltyCollected * (m.swArrears / totalBaseArrears);
                 srvPen = m.penaltyCollected * (m.serviceArrears / totalBaseArrears);
             }

             if (m.swArrears > 0) {
                 mathParts.push(`W/S: Rs.${(m.swArrears/100).toFixed(0)}`);
                 if (swPen > 0) mathParts.push(` -> Penalty: Rs.${(swPen/100).toFixed(0)}`);
             }
             if (m.serviceArrears > 0) {
                 mathParts.push(`Srv: Rs.${(m.serviceArrears/100).toFixed(0)}`);
                 if (srvPen > 0) mathParts.push(` -> Penalty: Rs.${(srvPen/100).toFixed(0)}`);
             }
             if (m.maintArrears > 0) {
                 mathParts.push(`Maint: Rs.${(m.maintArrears/100).toFixed(0)}`);
                 if (totalBaseArrears === 0 && m.penaltyCollected > 0) {
                     mathParts.push(` -> Penalty: Rs.${(m.penaltyCollected/100).toFixed(0)}`);
                 }
             }
          }

          // 2. ADVANCE BREAKDOWN
          if (m.advanceMonthsGenerated && m.advanceMonthsGenerated.length > 0) {
             if (mathParts.length > 0) mathParts.push(""); 
             mathParts.push(`[ADVANCE: ${m.advanceMonthsGenerated.length} Mo]`);
             
             let totalAdvBase = (m.swAdvance || 0) + (m.serviceAdvance || 0);
             let swDisc = 0, srvDisc = 0;
             if (totalAdvBase > 0 && m.advanceDiscount > 0) {
                 swDisc = m.advanceDiscount * (m.swAdvance / totalAdvBase);
                 srvDisc = m.advanceDiscount * (m.serviceAdvance / totalAdvBase);
             }

             if (m.swAdvance > 0) {
                 mathParts.push(`W/S: Rs.${(m.swAdvance/100).toFixed(0)}`);
                 if (swDisc > 0) mathParts.push(` -> Disc: -Rs.${(swDisc/100).toFixed(0)}`);
             }
             if (m.serviceAdvance > 0) {
                 mathParts.push(`Srv: Rs.${(m.serviceAdvance/100).toFixed(0)}`);
                 if (srvDisc > 0) mathParts.push(` -> Disc: -Rs.${(srvDisc/100).toFixed(0)}`);
             }
          }

          // 3. YEARLY MAINTENANCE BREAKDOWN
          if (m.advanceYearsGenerated && m.advanceYearsGenerated.length > 0) {
             if (mathParts.length > 0) mathParts.push("");
             mathParts.push(`[ADV YEARLY: ${m.advanceYearsGenerated.length} Yr]`);
             if (m.maintAdvance > 0) {
                 mathParts.push(`Maint: Rs.${(m.maintAdvance/100).toFixed(0)}`);
             }
          }

          // 4. HISTORICAL ADJUSTMENT (manual-settlement receipts whose real
          // collected total differs from what the billed months are worth -
          // see apply_manual_payments_batch4.py). Without this line the
          // Arrears/Advance figures above won't sum to Net Collection.
          if (m.historicalAdjustment) {
             if (mathParts.length > 0) mathParts.push("");
             if (m.historicalAdjustment > 0) {
                 mathParts.push(`Earlier Pendings: Rs.${(m.historicalAdjustment/100).toFixed(0)}`);
             } else {
                 mathParts.push(`Rate Adjustment: -Rs.${(Math.abs(m.historicalAdjustment)/100).toFixed(0)}`);
             }
          }
      }

      return [
        entry.dateOnly,
        `${(entry.propertyType || '').toUpperCase()} #${entry.houseNo}`,
        finalDesc, 
        mathParts.join("\n"),
        `${net.toFixed(2)}`,   
        `${fee.toFixed(2)}`,   
        `${gross.toFixed(2)}`  
      ];
    });

    // --- 4. GENERATE TABLE ---
    autoTable(doc, {
      startY: 44, 
      head: [['Date', 'Property', 'Description', 'Math Breakdown', 'Net Collection in Bank (Rs)', 'Gateway Fee (Rs)', 'Total Paid with Gateway fees (Rs)']],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        3: { cellWidth: 55 }, 
        4: { halign: 'right', fontStyle: 'bold', textColor: [39, 174, 96] }, 
        5: { halign: 'right', textColor: [120, 120, 120] }, 
        6: { halign: 'right', fontStyle: 'bold' } 
      },
      // --- 5. GRAND TOTALS FOOTER ---
      foot: [[
        { content: 'GRAND TOTALS', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10 } },
        { content: `${totalNet.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, textColor: [39, 174, 96] } },
        { content: `${totalFees.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, textColor: [120, 120, 120] } },
        { content: `${totalGross.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10 } }
      ]],
      footStyles: { fillColor: [240, 245, 250], textColor: [44, 62, 80] }
    });

    // --- 6. DOWNLOAD FILE ---
    // Safely format the filename based on what the admin actually filtered
    let fileName = "Ledger_Report";
    if (dateFilterType === "MONTH" && searchMonth) fileName += `_${searchMonth}`;
    else if (dateFilterType === "FY" && selectedFY) fileName += `_FY${selectedFY}`;
    
    doc.save(`${fileName}.pdf`);
  };

  return (
    <div className="ledger-wrapper">
      
      {/* HEADER */}
      <header className="ledger-header">
        <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>
            <h2 style={{margin: 0, color: '#2c3e50'}}>Master Financial Ledger</h2>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px'}}>
              <span style={{fontSize: '13px', color: '#7f8c8d', fontWeight: 'bold'}}>Active Financial Year:</span>
              <select 
                value={selectedFY} 
                onChange={e => setSelectedFY(e.target.value)}
                style={{padding: '4px 10px', borderRadius: '6px', border: '1px solid #ccc', fontWeight: 'bold', color: '#2c3e50'}}
              >
                {fyList.length === 0 && <option value={selectedFY}>1 April {selectedFY.substring(0,4)} to 31 March {selectedFY.substring(5,9)}</option>}
                {fyList.map(fy => <option key={fy} value={fy}>1 April {fy.substring(0,4)} to 31 March {fy.substring(5,9)}</option>)}
              </select>
            </div>
          </div>
          <button onClick={() => navigate('/admin')} style={{padding: '8px 16px', background: '#34495e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'}}>
            ← Back to Admin
          </button>
        </div>
      </header>

      <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 20px'}}>
        
       {/* --- EXPENSIVE TOP SUMMARY WIDGETS DELETED TO SAVE COSTS --- */}

        {/* --- MAIN CONTENT SPLIT --- */}
        <div className="ledger-content-grid">
          
          {/* --- LEFT COLUMN: RAW LEDGER --- */}
          <div className="fade-in-up" style={{animationDelay: '0.2s'}}>
            <div className="ledger-card">
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px', marginBottom: '15px'}}>
                <h3 style={{margin: 0, border: 'none', padding: 0}}>📖 Raw Ledger & Day-wise Log</h3>
                
                <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                  <button onClick={exportToPDF} disabled={filteredLedger.length === 0 || fetchingLedger} style={{ background: (filteredLedger.length === 0 || fetchingLedger) ? '#95a5a6' : '#2c3e50', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: (filteredLedger.length === 0 || fetchingLedger) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                    📥 Download Detailed PDF Report
                  </button>
                </div>
              </div>

              {/* --- NEW: SERVER-SIDE FILTER CONTROLS --- */}
              <div className="search-filter-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                <select value={dateFilterType} onChange={e => { setDateFilterType(e.target.value); setSearchMonth(""); setSearchDate(""); }} style={{fontWeight: 'bold', color: '#34495e', padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}>
                  <option value="MONTH">Specific Month</option>
                  <option value="DATE">Specific Date</option>
                  <option value="FY">Entire Financial Year</option>
                </select>

                {dateFilterType === "MONTH" && <input type="month" value={searchMonth} onChange={e => setSearchMonth(e.target.value)} style={{padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}} />}
                {dateFilterType === "DATE" && <input type="date" value={searchDate} onChange={e => setSearchDate(e.target.value)} style={{padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}} />}
                {dateFilterType === "FY" && (
                  <select value={selectedFY} onChange={(e) => setSelectedFY(e.target.value)} style={{padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}>
                    {fyList.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                  </select>
                )}
                
                <select value={searchPropType} onChange={e => setSearchPropType(e.target.value)} style={{padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}>
                  <option value="ALL">All Properties</option>
                  <option value="houses">Houses</option>
                  <option value="shops">Shops</option>
                  <option value="booths">Booths</option>
                  <option value="c_buildings">Comm. Buildings</option>
                  <option value="n_home">Nursing Homes</option>
                </select>
                
                <input type="text" placeholder="Plot No." value={searchHouse} onChange={e => setSearchHouse(e.target.value)} style={{width: '90px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}} />

                <button 
                  onClick={handleFetchLedger} 
                  disabled={fetchingLedger}
                  style={{ background: fetchingLedger ? '#7f8c8d' : '#27ae60', color: 'white', padding: '8px 16px', borderRadius: '4px', border: 'none', cursor: fetchingLedger ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
                >
                  {fetchingLedger ? 'Fetching...' : '🔍 Search'}
                </button>
              </div>
              
              <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                <table className="ledger-table">
                  <thead><tr><th>Date</th><th>Property</th><th>Description</th><th style={{textAlign: 'right'}}>Amount</th></tr></thead>
                  <tbody>
                    {filteredLedger.length === 0 ? <tr><td colSpan="6" style={{textAlign:'center', padding: '20px', color: '#888'}}>No payment entries found matching your filters.</td></tr> : null}
                    {filteredLedger.map((entry) => (
                      <React.Fragment key={entry.id}>
                        <tr style={{ background: expandedRows[entry.id] ? '#f4f6f8' : 'white', borderBottom: expandedRows[entry.id] ? 'none' : '1px solid #eee' }}>
                          <td>{entry.dateOnly}</td>
                          <td style={{fontWeight: 'bold', color: '#2c3e50'}}>{formatProp(entry.propertyType)} #{entry.houseNo}</td>
                          <td>
                            <span className={`badge-type ${entry.type === 'PAYMENT' ? 'success' : 'danger'}`}>{entry.type}</span>
                          </td>
                          <td style={{color: '#555'}}>
                            <div>{entry.description || entry.type}</div>
                            {entry.referenceId && (
                              <div style={{fontSize: '10px', color: '#999', marginTop: '4px', fontFamily: 'monospace', background: '#f4f4f4', padding: '2px 6px', borderRadius: '4px', display: 'inline-block'}}>
                                Ref: {entry.referenceId}
                              </div>
                            )}
                            {entry.metrics && (
                              <div style={{marginTop: '6px'}}>
                                <button onClick={() => toggleRow(entry.id)} style={{background: 'none', border: 'none', color: '#3498db', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', padding: 0}}>
                                  {expandedRows[entry.id] ? '▲ Hide Math' : '▼ View Math'}
                                </button>
                              </div>
                            )}
                          </td>
                          <td style={{fontWeight: 'bold', color: entry.type === 'PAYMENT' ? '#27ae60' : '#e74c3c'}}>
                            {entry.type === 'PAYMENT' ? '+' : '-'} ₹{(entry.amountSigned / 100).toFixed(2)}
                          </td>
                        </tr>

                        {expandedRows[entry.id] && entry.metrics && (
                          <tr style={{ background: '#f4f6f8' }}>
                            <td colSpan="5" style={{ padding: '0 20px 20px 20px', borderBottom: '2px solid #ddd' }}>
                              <div style={{ background: 'white', border: '1px dashed #ccc', borderRadius: '6px', padding: '15px', display: 'flex', gap: '30px', fontSize: '12px', color: '#444' }}>
                                <div style={{ flex: 1 }}>
                                  <strong style={{ color: '#2c3e50', display: 'block', marginBottom: '8px' }}>📅 Months Cleared</strong>
                                  {entry.metrics.arrearsMonthsCleared?.length > 0 && (<div style={{ marginBottom: '8px' }}><span style={{ color: '#7f8c8d' }}>Arrears: </span>{entry.metrics.arrearsMonthsCleared.join(', ')}</div>)}
                                  {entry.metrics.advanceMonthsGenerated?.length > 0 && (<div><span style={{ color: '#27ae60' }}>Advance: </span>{entry.metrics.advanceMonthsGenerated.join(', ')}</div>)}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <strong style={{ color: '#2c3e50', display: 'block', marginBottom: '8px' }}>🧮 Exact Calculation</strong>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: '4px' }}>
                                    {entry.metrics.baseArrearsCollected > 0 && (<><span>Base Arrears:</span> <span>₹{(entry.metrics.baseArrearsCollected / 100).toFixed(2)}</span></>)}
                                    {entry.metrics.penaltyCollected > 0 && (<><span>Late Penalties:</span> <span>₹{(entry.metrics.penaltyCollected / 100).toFixed(2)}</span></>)}
                                    {entry.metrics.advanceCollected > 0 && (<><span>Advance Base:</span> <span>₹{((entry.metrics.advanceCollected + (entry.metrics.advanceDiscount || 0)) / 100).toFixed(2)}</span></>)}
                                    {entry.metrics.advanceDiscount > 0 && (<><span style={{color: '#27ae60'}}>Advance Discount:</span> <span style={{color: '#27ae60'}}>-₹{(entry.metrics.advanceDiscount / 100).toFixed(2)}</span></>)}
                                    {entry.metrics.platformFee > 0 && (<><span style={{color: '#7f8c8d'}}>Gateway Fee:</span> <span style={{color: '#7f8c8d'}}>₹{(entry.metrics.platformFee / 100).toFixed(2)}</span></>)}
                                    {!!entry.metrics.historicalAdjustment && (
                                      <>
                                        <span style={{color: entry.metrics.historicalAdjustment > 0 ? '#2c3e50' : '#e67e22'}}>
                                          {entry.metrics.historicalAdjustment > 0 ? 'Earlier Pendings:' : 'Rate Adjustment:'}
                                        </span>
                                        <span style={{color: entry.metrics.historicalAdjustment > 0 ? '#2c3e50' : '#e67e22'}}>
                                          {entry.metrics.historicalAdjustment > 0 ? '+' : '-'}₹{(Math.abs(entry.metrics.historicalAdjustment) / 100).toFixed(2)}
                                        </span>
                                      </>
                                    )}
                                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #eee', margin: '4px 0' }}></div>
                                    <strong style={{color: '#2c3e50'}}>Grand Total:</strong> 
                                    <strong style={{color: '#2c3e50'}}>₹{(entry.amountSigned / 100).toFixed(2)}</strong>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* --- RIGHT COLUMN: DEFAULTERS --- */}
          <div className="fade-in-up" style={{animationDelay: '0.3s'}}>
            <div className="ledger-card" style={{borderTop: '5px solid #e74c3c'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: '15px'}}>
                <div>
                  <h3 style={{color: '#c0392b', margin: 0, border: 'none', padding: 0}}>🚨 Top Defaulters</h3>
                  <p style={{fontSize: '13px', color: '#c0392b', marginTop: '5px', marginBottom: 0, fontWeight: 'bold'}}>
                    Total Pending: ₹ {filteredDefaulters.reduce((sum, def) => sum + (def.due || 0), 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <select value={defaulterPropType} onChange={e => setDefaulterPropType(e.target.value)} style={{padding: '4px 8px', borderRadius: '4px', border: '1px solid #e74c3c', fontSize: '11px', color: '#c0392b', fontWeight: 'bold', background: '#fff'}}>
                  <option value="ALL">All Types</option><option value="houses">Houses</option><option value="shops">Shops</option><option value="booths">Booths</option><option value="c_buildings">Comm. Bldgs</option><option value="n_home">Nursing Homes</option>
                </select>
              </div>
              <div style={{maxHeight: '480px', overflowY: 'auto', paddingRight: '10px'}}>
                {filteredDefaulters.length === 0 ? <p style={{color: '#27ae60', fontWeight: 'bold', textAlign: 'center', marginTop: '20px'}}>Zero pending dues! 🎉</p> : (
                  filteredDefaulters.map(def => (
                    <div key={`${def.propertyType}_${def.houseNo}`} className="defaulter-item">
                      <div className="defaulter-info">
                        <h4>{formatProp(def.propertyType)} #{def.houseNo}</h4>
                        {/* Added safety checks for monthsPending array */}
                        <p>{def.monthsPending?.length || 0} month(s) pending</p>
                        <p style={{fontSize: '10px', marginTop: '3px'}}>({(def.monthsPending || []).slice(0, 3).join(', ')}{(def.monthsPending?.length > 3) ? '...' : ''})</p>
                      </div>
                      {/* NEW: Safely reads 'def.due' instead of 'def.totalDue' */}
                      <div className="defaulter-amount">₹ {(def.due || 0).toLocaleString('en-IN')}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* --- HIGH-DENSITY CAPSULE MATRIX & DRILL-DOWN MODAL --- */}
        {/* ========================================================= */}
        
        {(() => {
          // Extract the FY start year
          const fyStartYear = selectedFY ? parseInt(selectedFY.substring(0, 4)) : new Date().getFullYear();
          const matrixTargetYear = (fyStartYear - 1).toString();
          const hasTarget = matrixView === 'MONTHLY' ? gridMonth : matrixTargetYear;

          // Process Metadata directly (No loops through raw bills!)
          let summary = { totalPlotsBilled: 0, expectedRevenue: 0, collectedRevenue: 0, pendingRevenue: 0, paidCount: 0, unpaidCount: 0 };
          const categorizedPlots = {};
          
          if (matrixData) {
              summary = matrixData.summary || summary;
              
              if (matrixData.grid) {
                  // NEW: Use Object.entries to safely extract the exact property type and house number from the key
                  Object.entries(matrixData.grid).forEach(([ownerKey, plot]) => {
                      const lastUnderscore = ownerKey.lastIndexOf('_');
                      const fallbackPType = ownerKey.substring(0, lastUnderscore);
                      const fallbackHNo = ownerKey.substring(lastUnderscore + 1);

                      const pType = plot.pType || fallbackPType;
                      const hNo = plot.hNo || fallbackHNo;

                      if (gridPropType === "ALL" || gridPropType === pType) {
                          if (!categorizedPlots[pType]) categorizedPlots[pType] = [];
                          categorizedPlots[pType].push({
                              pType: pType,
                              hNo: hNo,
                              status: plot.status,
                              totalDue: (plot.baseDue || 0) + (plot.penalty || 0),
                              baseDue: plot.baseDue || 0,
                              penalty: plot.penalty || 0,
                              paidAt: plot.paidAt,
                              receiptId: plot.receiptId
                          });
                      }
                  });
              }
              // Sort numerically
              Object.keys(categorizedPlots).forEach(pType => {
                  categorizedPlots[pType].sort((a,b) => parseInt(a.hNo) - parseInt(b.hNo));
              });
          }

          return (
            <div className="matrix-card fade-in-up" style={{animationDelay: '0.4s', padding: '25px', background: '#fff', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', marginTop: '20px', position: 'relative'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px'}}>
                    <div><h3 style={{margin: 0, color: '#0f172a', fontSize: '20px', fontWeight: '900'}}>🎯 High-Density Property Matrix</h3></div>
                    <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
                        <div style={{background: '#f1f5f9', borderRadius: '20px', padding: '4px', display: 'flex'}}>
                            <button onClick={()=>setMatrixView('MONTHLY')} style={{background: matrixView === 'MONTHLY' ? '#fff' : 'transparent', border:'none', padding:'6px 16px', borderRadius:'16px', fontWeight:'bold', color: matrixView==='MONTHLY' ? '#2c3e50':'#94a3b8', cursor:'pointer', boxShadow: matrixView==='MONTHLY' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'}}>Monthly Bills</button>
                            <button onClick={()=>setMatrixView('YEARLY')} style={{background: matrixView === 'YEARLY' ? '#fff' : 'transparent', border:'none', padding:'6px 16px', borderRadius:'16px', fontWeight:'bold', color: matrixView==='YEARLY' ? '#2c3e50':'#94a3b8', cursor:'pointer', boxShadow: matrixView==='YEARLY' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'}}>Yearly Maint.</button>
                        </div>
                        
                        {matrixView === 'MONTHLY' ? (
                            <select value={gridMonth} onChange={e => setGridMonth(e.target.value)} style={{padding: '8px 16px', borderRadius: '20px', border: '1px solid #e2e8f0', fontWeight: 'bold', color: '#334155', background: '#f8fafc', cursor: 'pointer', outline: 'none'}}>
                                {monthList.length === 0 && <option value="">No data</option>}
                                {monthList.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        ) : (
                            <div style={{padding: '8px 16px', borderRadius: '20px', border: '1px solid #c8e6c9', fontWeight: 'bold', color: '#2e7d32', background: '#e8f5e9'}}>
                                FY Maintenance: 1 Oct {fyStartYear - 1} to 30 Sept {fyStartYear}
                            </div>
                        )}
                        <select value={gridPropType} onChange={e => setGridPropType(e.target.value)} style={{padding: '8px 16px', borderRadius: '20px', border: '1px solid #e2e8f0', fontWeight: 'bold', color: '#334155', background: '#f8fafc', cursor: 'pointer', outline: 'none'}}>
                            <option value="ALL">All Properties</option><option value="houses">Houses</option><option value="shops">Shops</option><option value="booths">Booths</option><option value="c_buildings">Comm. Buildings</option><option value="n_home">Nursing Homes</option>
                        </select>
                        <button 
                            onClick={handleFetchMatrix} 
                            style={{ background: '#27ae60', color: 'white', padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            🔍 Search
                        </button>
                    </div>
                </div>

                {!hasTarget ? (<div style={{padding: '40px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic'}}>Select a period to view the matrix.</div>) 
                : !matrixData ? (<div style={{padding: '40px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic'}}>No metadata generated for this period yet.</div>) 
                : (
                    <>
                    <div style={{display: 'flex', justifyContent: 'space-between', background: '#f8fafc', padding: '16px 24px', borderRadius: '12px', marginBottom: '30px', border: '1px solid #f1f5f9'}}>
                        <div style={{textAlign: 'center'}}><div style={{fontSize:'11px', color:'#64748b', fontWeight:'bold', textTransform:'uppercase'}}>Plots</div><div style={{fontSize:'18px', fontWeight:'900', color:'#0f172a'}}>{summary.totalPlotsBilled}</div></div>
                        <div style={{width:'1px', background:'#e2e8f0'}}></div>
                        <div style={{textAlign: 'center'}}><div style={{fontSize:'11px', color:'#64748b', fontWeight:'bold', textTransform:'uppercase'}}>Expected Revenue</div><div style={{fontSize:'18px', fontWeight:'900', color:'#3b82f6'}}>₹ {summary.expectedRevenue?.toLocaleString('en-IN') || 0}</div></div>
                        <div style={{width:'1px', background:'#e2e8f0'}}></div>
                        <div style={{textAlign: 'center'}}><div style={{fontSize:'11px', color:'#64748b', fontWeight:'bold', textTransform:'uppercase'}}>Collected</div><div style={{fontSize:'18px', fontWeight:'900', color:'#10b981'}}>₹ {summary.collectedRevenue?.toLocaleString('en-IN') || 0}</div></div>
                        <div style={{width:'1px', background:'#e2e8f0'}}></div>
                        <div style={{textAlign: 'center'}}><div style={{fontSize:'11px', color:'#64748b', fontWeight:'bold', textTransform:'uppercase'}}>Pending</div><div style={{fontSize:'18px', fontWeight:'900', color:'#ef4444'}}>₹ {summary.pendingRevenue?.toLocaleString('en-IN') || 0}</div></div>
                        <div style={{width:'1px', background:'#e2e8f0'}}></div>
                        <div style={{textAlign: 'center', display:'flex', alignItems:'center', gap:'10px'}}>
                            <div style={{display:'flex', alignItems:'center', gap:'4px'}}><div style={{width:'8px',height:'8px',borderRadius:'50%',background:'#10b981'}}></div><span style={{fontSize:'12px', fontWeight:'bold'}}>{summary.paidCount}</span></div>
                            <div style={{display:'flex', alignItems:'center', gap:'4px'}}><div style={{width:'8px',height:'8px',borderRadius:'50%',background:'#ef4444'}}></div><span style={{fontSize:'12px', fontWeight:'bold'}}>{summary.unpaidCount}</span></div>
                        </div>
                    </div>

                    <div style={{maxHeight: '500px', overflowY: 'auto', paddingRight: '10px'}}>
                        {Object.entries(categorizedPlots).map(([pType, plots]) => {
                            if (plots.length === 0) return null;
                            return (
                                <div key={pType} style={{marginBottom: '25px'}}>
                                    <div style={{fontSize: '12px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '1px'}}>{formatProp(pType)} ({plots.length})</div>
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                                        {plots.map(plot => {
                                            let bg = '#f8fafc'; let color = '#cbd5e1'; let border = '#f1f5f9';
                                            if (plot.status === 'PAID') { bg = '#dcfce7'; color = '#15803d'; border = '#86efac'; }
                                            else if (plot.status === 'ADVANCE') { bg = '#dbeafe'; color = '#1d4ed8'; border = '#93c5fd'; }
                                            else if (plot.status === 'UNPAID') { bg = '#fee2e2'; color = '#b91c1c'; border = '#fca5a5'; }
                                            return (
                                                <button key={plot.hNo} onClick={() => setSelectedPlotDetails(plot)} style={{width: '42px', height: '34px', borderRadius: '8px', background: bg, color: color, border: `1px solid ${border}`, fontSize: '12px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease'}} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'} title={`Plot ${plot.hNo} - ${plot.status.replace('_', ' ')}`}>{plot.hNo}</button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    </>
                )}

                {selectedPlotDetails && (
                    <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'}} onClick={() => setSelectedPlotDetails(null)}>
                        <div style={{background: '#fff', width: '100%', maxWidth: '380px', borderRadius: '20px', padding: '30px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', animation: 'fadeInUp 0.3s ease'}} onClick={e => e.stopPropagation()}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px'}}>
                                <div><div style={{fontSize: '24px', fontWeight: '900', color: '#0f172a'}}>Plot #{selectedPlotDetails.hNo}</div><div style={{fontSize: '13px', color: '#64748b', fontWeight: 'bold'}}>{formatProp(selectedPlotDetails.pType)} | {hasTarget}</div></div>
                                <button onClick={() => setSelectedPlotDetails(null)} style={{background: '#f1f5f9', border: 'none', width: '30px', height: '30px', borderRadius: '50%', color: '#64748b', fontWeight: 'bold', cursor: 'pointer'}}>✕</button>
                            </div>
                            
                            <div>
                                <div style={{marginBottom: '20px'}}>
                                    <span style={{padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '900', letterSpacing: '0.5px', background: selectedPlotDetails.status === 'PAID' ? '#dcfce7' : selectedPlotDetails.status === 'ADVANCE' ? '#dbeafe' : '#fee2e2', color: selectedPlotDetails.status === 'PAID' ? '#15803d' : selectedPlotDetails.status === 'ADVANCE' ? '#1d4ed8' : '#b91c1c'}}>
                                        {selectedPlotDetails.status === 'PAID' ? '✅ PAID ON TIME' : selectedPlotDetails.status === 'ADVANCE' ? '🚀 PAID IN ADVANCE' : '⚠️ UNPAID DEFAULTER'}
                                    </span>
                                </div>
                                <div style={{background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#475569'}}><span>Base Charges:</span> <span>₹ {selectedPlotDetails.baseDue?.toLocaleString('en-IN') || 0}</span></div>
                                    {selectedPlotDetails.penalty > 0 && (<div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#ef4444'}}><span>Late Penalty:</span> <span>+ ₹ {selectedPlotDetails.penalty.toLocaleString('en-IN')}</span></div>)}
                                    <div style={{borderTop: '1px dashed #cbd5e1', margin: '12px 0'}}></div>
                                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '900', color: '#0f172a'}}><span>{selectedPlotDetails.status === 'UNPAID' ? 'Total Due:' : 'Amount Paid:'}</span> <span>₹ {selectedPlotDetails.totalDue?.toLocaleString('en-IN') || 0}</span></div>
                                </div>
                                {(selectedPlotDetails.status === 'PAID' || selectedPlotDetails.status === 'ADVANCE') && selectedPlotDetails.receiptId && (
                                    <div style={{marginTop: '20px', fontSize: '12px', color: '#64748b'}}>
                                        <div style={{marginBottom: '6px'}}><strong style={{color: '#334155'}}>Clearance Date:</strong> {selectedPlotDetails.paidAt ? new Date(selectedPlotDetails.paidAt).toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit'}) : 'Manual Entry'}</div>
                                        <div><strong style={{color: '#334155'}}>Receipt/Ref ID:</strong></div>
                                        <div style={{marginTop: '4px', fontFamily: 'monospace', background: '#f1f5f9', padding: '8px', borderRadius: '6px', wordBreak: 'break-all'}}>{selectedPlotDetails.receiptId}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
          );
        })()}

      </div>
    </div>
  );
}

export default AdminLedger;